import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ClipboardCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Onboarding checklist for one client. Items are sourced from the backend's
 * DEFAULT_CHECKLIST_ITEMS catalog and grouped by category. Admins can tick
 * boxes + add per-item notes. Progress is rendered as a percentage strip.
 */
export default function ClientChecklistPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { clients } = useClients();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const company = useMemo(
    () => clients.find((c) => c.client_id === clientId)?.company_name || data?.company_name || "—",
    [clients, clientId, data]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${clientId}/checklist`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load checklist"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const patch = async (key, payload) => {
    setSavingKey(key);
    try {
      const { data } = await api.patch(`/clients/${clientId}/checklist/${key}`, payload);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingKey(null); }
  };

  const toggle = (key, current) => patch(key, { checked: !current });
  const saveNotes = (key, notes) => patch(key, { notes });

  // Group items by their `group` field, preserving the catalog order.
  const grouped = useMemo(() => {
    if (!data?.items) return [];
    const seen = new Map();
    data.items.forEach((it) => {
      if (!seen.has(it.group)) seen.set(it.group, []);
      seen.get(it.group).push(it);
    });
    return [...seen.entries()].map(([group, items]) => ({ group, items }));
  }, [data]);

  if (loading || !data) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="checklist-loading">Loading checklist…</div>;
  }

  const pct = data.total ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="checklist-page">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" className="rounded-sm -ml-2 mb-2"
          onClick={() => navigate(`/clients/${clientId}`)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to client
        </Button>
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-sm bg-[#002FA7] text-white grid place-items-center shrink-0">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline">Onboarding</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight">
              Checklist · {company}
            </h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Track everything required when onboarding this client — analytics access,
              ad-platform connections, brand assets and strategy docs.
            </p>
          </div>
        </div>
      </div>

      {/* Progress strip */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#002FA7]" />
              <div className="label-overline">Progress</div>
              <span className="text-[11px] font-mono text-zinc-600"
                data-testid="checklist-progress-label">
                {data.completed} / {data.total} items complete
              </span>
            </div>
            <span className="font-display font-bold text-2xl tabular-nums text-[#002FA7]"
              data-testid="checklist-progress-pct">{pct}%</span>
          </div>
          <div className="h-2 rounded-sm bg-zinc-100 overflow-hidden">
            <div className="h-full bg-[#002FA7] transition-all duration-300"
              style={{ width: `${pct}%` }}
              data-testid="checklist-progress-bar" />
          </div>
        </CardContent>
      </Card>

      {/* Grouped items */}
      {grouped.map(({ group, items }) => (
        <Card key={group} className="rounded-sm border-zinc-200" data-testid={`checklist-group-${group}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-overline">{group}</div>
              <span className="text-[10px] font-mono text-zinc-400">
                {items.filter((i) => i.checked).length} / {items.length} done
              </span>
            </div>
            <div className="space-y-2">
              {items.map((it) => (
                <ChecklistRow key={it.key} item={it}
                  busy={savingKey === it.key}
                  onToggle={() => toggle(it.key, it.checked)}
                  onSaveNotes={(notes) => saveNotes(it.key, notes)} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChecklistRow({ item, busy, onToggle, onSaveNotes }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [dirty, setDirty] = useState(false);

  // Resync local notes when the server payload changes (e.g. after toggle)
  useEffect(() => { setNotes(item.notes || ""); setDirty(false); }, [item.notes]);

  return (
    <div
      className={`grid grid-cols-[24px_1fr_auto] gap-3 items-center p-2.5 rounded-sm border
                  transition-colors ${
        item.checked
          ? "border-emerald-200 bg-emerald-50/50"
          : "border-zinc-200 bg-white hover:bg-zinc-50"
      }`}
      data-testid={`checklist-row-${item.key}`}>
      <Checkbox
        checked={item.checked}
        onCheckedChange={onToggle}
        disabled={busy}
        data-testid={`checklist-toggle-${item.key}`}
      />
      <div className="min-w-0">
        <div className={`text-sm ${item.checked ? "line-through text-zinc-500" : "text-zinc-900"}`}>
          {item.label}
        </div>
        {item.completed_at && (
          <div className="font-mono text-[10px] text-emerald-700 mt-0.5"
            data-testid={`checklist-completed-${item.key}`}>
            ✓ {new Date(item.completed_at).toLocaleString()}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
          onBlur={() => { if (dirty) { onSaveNotes(notes); setDirty(false); } }}
          className="rounded-sm h-8 text-xs w-[200px]"
          data-testid={`checklist-notes-${item.key}`}
        />
      </div>
    </div>
  );
}
